import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SeoHead from './SeoHead';
import SiteFooter from './SiteFooter';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <SeoHead
        title="Algemene Voorwaarden | Swapify"
        description="Lees de algemene voorwaarden van Swapify voor het kopen en verkopen van cadeaubonnen."
        canonicalPath="/terms"
      />
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
           {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-2xl font-bold bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
              Swapify
            </div>
          </Link>
            <Link to="/" className="text-gray-700 hover:text-red-500 transition flex items-center space-x-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Terug naar home</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Algemene Voorwaarden</h1>
        <p className="text-gray-500 mb-8">Laatst bijgewerkt: februari 2026</p>

        <div className="bg-white rounded-2xl shadow-lg p-8 prose prose-lg max-w-none">

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Algemeen</h2>
          <p className="text-gray-700 mb-4">
            Deze website en dienst worden aangeboden door Swapify Marketplace ("Swapify").
            Door gebruik te maken van onze website en diensten ga je akkoord met deze Algemene Voorwaarden.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Definities</h2>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li><strong>Platform:</strong> De website www.swapify.nl en de daarop aangeboden diensten.</li>
            <li><strong>Verkoper:</strong> Een gebruiker die via het Platform een cadeaubon aanbiedt voor verkoop.</li>
            <li><strong>Koper:</strong> Een gebruiker die via het Platform een cadeaubon koopt.</li>
            <li><strong>Gebruiker:</strong> Iedere natuurlijke persoon of rechtspersoon die gebruik maakt van het Platform.</li>
            <li><strong>Cadeaubon:</strong> Een door een derde uitgegeven digitale of fysieke tegoedbon die een geldwaarde vertegenwoordigt en kan worden ingewisseld bij een winkel, webshop of dienstverlener.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Dienstverlening en Platform Rol</h2>
          <p className="text-gray-700 mb-4">
            Swapify faciliteert de aan- en verkoop van digitale cadeaubonnen tussen gebruikers. Swapify treedt op als bemiddelaar, maar is geen partij bij de transactie tussen koper en verkoper.
          </p>
          <p className="text-gray-700 mb-4">
            Wanneer een verkoper een cadeaubon aanbiedt op het Platform, biedt de verkoper deze aan voor verkoop aan kopers. Wanneer een koper een cadeaubon koopt, komt er een overeenkomst tot stand tussen de verkoper en de koper. Swapify faciliteert deze transactie door:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>De betaling van de koper in ontvangst te nemen via een betalingsdienstverlener</li>
            <li>De cadeauboncode over te dragen aan de koper na verificatie</li>
            <li>De uitbetaling aan de verkoper te verwerken na succesvolle afronding</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Toegestane Cadeaubonnen</h2>
          <p className="text-gray-700 mb-4">
            Niet alle cadeaubonnen kunnen via het Platform worden aangeboden. Een cadeaubon moet voldoen aan de volgende eisen:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>Minimaal 30 dagen geldig na aanbieding</li>
            <li>Minimaal saldo van €20 en maximaal €150</li>
            <li>Het saldo moet verifieerbaar zijn door Swapify</li>
            <li>De cadeaubon mag niet gestolen, frauduleus verkregen of reeds (gedeeltelijk) gebruikt zijn</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Swapify behoudt zich het recht voor om cadeaubonnen te weigeren die niet aan deze criteria voldoen of waarbij verificatie niet mogelijk is.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Transactielimieten</h2>
          <p className="text-gray-700 mb-4">
            Om fraude en misbruik tegen te gaan hanteert Swapify limieten op het aantal en de waarde van transacties. 
            Deze limieten kunnen variëren op basis van accountleeftijd, verkoopgeschiedenis en andere factoren. 
            Swapify behoudt zich het recht voor om accounts te blokkeren die verdachte verkooppatronen vertonen.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Uitbetaling aan Verkopers</h2>
          <p className="text-gray-700 mb-4">
            Swapify betaalt verkopers doorgaans binnen 24 uur na succesvolle verificatie van de cadeaubon uit. 
            Voor nieuwe verkopers kan een langere verwerkingstijd gelden.
          </p>
          <p className="text-gray-700 mb-4">
            Als tijdens verificatie blijkt dat de cadeaubon ongeldig is, een andere waarde heeft dan opgegeven, 
            of op frauduleuze wijze is verkregen, wordt de transactie geannuleerd en ontvangt de verkoper geen betaling.
          </p>
          <p className="text-gray-700 mb-4">
            Swapify kan reeds uitgekeerde bedragen terugvorderen als achteraf blijkt dat de cadeaubon ongeldig was of onjuist is aangeboden.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Verplichtingen Verkoper</h2>
          <p className="text-gray-700 mb-4">De verkoper verklaart en garandeert dat:</p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>hij de rechtmatige eigenaar is van de cadeaubon;</li>
            <li>de cadeaubon volledig geldig is en het opgegeven saldo bevat;</li>
            <li>de cadeaubon niet op frauduleuze wijze is verkregen;</li>
            <li>de cadeaubon niet gestolen, vervalst, geblokkeerd of (deels) gebruikt is;</li>
            <li>alle verstrekte informatie volledig en juist is;</li>
            <li>hij geen meerdere accounts gebruikt om beperkingen te omzeilen;</li>
            <li>hij de cadeaubon niet elders heeft aangeboden of zal aanbieden tijdens de verkoopperiode.</li>
          </ul>
          <p className="text-gray-700 mb-4">Bij (vermoeden van) fraude of misbruik kan Swapify:</p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>uitbetalingen opschorten, blokkeren of terugvorderen;</li>
            <li>accounts tijdelijk of permanent blokkeren;</li>
            <li>extra verificatie of documentatie eisen;</li>
            <li>aangifte doen bij de bevoegde autoriteiten;</li>
            <li>informatie delen met betalingsdienstverleners en autoriteiten.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Cadeaubonnen Kopen</h2>
          <p className="text-gray-700 mb-4">
            Gebruikers kunnen via het Platform digitale cadeaubonnen rechtstreeks van Swapify aanschaffen. 
            Na afronding van de betaling ontvangt de koper:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>Direct toegang tot de cadeauboncode via de website</li>
            <li>Een bevestigingsmail met de cadeauboncode en gebruiksinstructies</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Vanwege de directe overdraagbaarheid van digitale cadeaubonnen is retourneren of ruilen niet mogelijk. 
            Swapify kan in bijzondere gevallen, naar eigen beoordeling, afwijken van dit beleid. 
            Hiervoor kan contact worden opgenomen via hello@swapify.nl.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Klachten over Ongeldige Cadeaubonnen</h2>
          <p className="text-gray-700 mb-4">
            Hoewel cadeaubonnen niet kunnen worden geretourneerd, hebben kopers <strong>12 uur na aankoop</strong> de 
            mogelijkheid om te melden dat een cadeaubon ongeldig is of reeds gebruikt blijkt te zijn.
          </p>
          <p className="text-gray-700 mb-2"><strong>Hoe een klacht indienen:</strong></p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>Per e-mail naar hello@swapify.nl met je bestelnummer</li>
          </ul>
          <p className="text-gray-700 mb-2"><strong>Bij een klacht:</strong></p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>Wordt de uitbetaling aan de verkoper direct geblokkeerd</li>
            <li>Moet de koper bewijs leveren (bijvoorbeeld een screenshot van een foutmelding bij de retailer)</li>
            <li>Krijgt de verkoper de mogelijkheid om te reageren</li>
            <li>Beoordeelt Swapify de situatie binnen 72 uur</li>
          </ul>
          <p className="text-gray-700 mb-2"><strong>Mogelijke uitkomsten:</strong></p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li><strong>Klacht gegrond:</strong> De koper ontvangt een volledige terugbetaling en de verkoper wordt gesanctioneerd</li>
            <li><strong>Klacht ongegrond:</strong> De betaling wordt vrijgegeven aan de verkoper</li>
            <li><strong>Onduidelijk:</strong> Swapify kan een maatwerk-oplossing voorstellen</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Na 12 uur: indien geen klacht is ingediend binnen 12 uur na aankoop, wordt de transactie als succesvol 
            beschouwd en is terugbetaling niet meer mogelijk. Kopers die herhaaldelijk ongegronde klachten indienen 
            kunnen worden geblokkeerd.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Kosten en Prijsstelling</h2>
          <p className="text-gray-700 mb-4">
            Swapify rekent geen verborgen service- of transactiekosten. Eventuele kosten, zoals platformkosten 
            of kosten voor betaalmethoden, worden altijd vooraf en duidelijk getoond tijdens het afrekenen.
          </p>
          <p className="text-gray-700 mb-4">
            De verkoper bepaalt zelf het gewenste netto-uitbetalingsbedrag. Op basis hiervan berekent Swapify 
            automatisch de uiteindelijke verkoopprijs voor de koper, inclusief platformkosten.
          </p>
          <p className="text-gray-700 mb-4">
            Swapify hanteert een maximale verkoopprijs, vastgesteld als een percentage van het saldo van de cadeaubon. 
            De verkoop van cadeaubonnen aan Swapify is vrijgesteld van btw.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Privacy</h2>
          <p className="text-gray-700 mb-4">
            Swapify verwerkt persoonsgegevens zoals naam, e-mailadres, betaalgegevens, IP-adres, 
            cadeaubon-informatie en transactiegeschiedenis. Deze gegevens worden gebruikt voor het uitvoeren 
            van transacties, fraudedetectie, verificatie en naleving van wettelijke verplichtingen.
          </p>
          <p className="text-gray-700 mb-4">
            Meer informatie staat in onze{' '}
            <Link to="/privacy" className="text-green-600 hover:underline font-semibold">Privacyverklaring</Link>.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Aansprakelijkheid</h2>
          <p className="text-gray-700 mb-2"><strong>Swapify is aansprakelijk voor:</strong></p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>Fouten in het Platform of de betalingsverwerking die aantoonbaar door Swapify zijn veroorzaakt</li>
            <li>Onjuiste afhandeling van het klachtenproces</li>
            <li>Nalatigheid in de uitvoering van verificaties</li>
          </ul>
          <p className="text-gray-700 mb-2"><strong>Swapify is niet aansprakelijk voor:</strong></p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>De geldigheid, waarde of bruikbaarheid van cadeaubonnen na afloop van de 12-uurs verificatieperiode</li>
            <li>Handelingen van individuele gebruikers</li>
            <li>Indirecte schade, gevolgschade, gemiste inkomsten of reputatieschade</li>
            <li>Schade voortvloeiend uit het gebruik van een cadeaubon na succesvolle transactie</li>
          </ul>
          <p className="text-gray-700 mb-4">
            De totale aansprakelijkheid van Swapify is in alle gevallen beperkt tot het bedrag van de betreffende transactie.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Fraudepreventie en Monitoring</h2>
          <p className="text-gray-700 mb-4">
            Swapify monitort actief op frauduleuze activiteiten en witwaspraktijken. Dit omvat onder andere:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>Controle op verkooppatronen en -frequentie</li>
            <li>Monitoring van device- en IP-gebruik</li>
            <li>Detectie van duplicate accounts</li>
            <li>Analyse van transactiegedrag</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Bij signalen van fraude of witwassen kan Swapify onmiddellijk actie ondernemen, waaronder het blokkeren 
            van accounts en het vasthouden van uitbetalingen. Swapify werkt samen met betalingsdienstverleners en 
            autoriteiten bij de bestrijding van fraude en witwassen.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">15. Wijzigingen</h2>
          <p className="text-gray-700 mb-4">
            Swapify behoudt zich het recht voor deze Algemene Voorwaarden op elk moment te wijzigen. 
            De meest actuele versie staat altijd op de website. Door de diensten te blijven gebruiken na 
            een wijziging, ga je akkoord met de aangepaste voorwaarden.
          </p>
          <p className="text-gray-700 mb-4">
            Ingrijpende wijzigingen worden minimaal 14 dagen van tevoren aangekondigd via e-mail of een melding op het Platform.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">16. Toepasselijk Recht en Geschillen</h2>
          <p className="text-gray-700 mb-4">
            Op deze Algemene Voorwaarden is Nederlands recht van toepassing. 
            Geschillen die niet onderling kunnen worden opgelost, worden voorgelegd aan de bevoegde rechter te Amsterdam.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">17. Contact</h2>
          <p className="text-gray-700 mb-4">
            Voor vragen, klachten of opmerkingen kun je contact opnemen via:{' '}
            <a href="mailto:hello@swapify.nl" className="text-green-600 hover:underline font-semibold">hello@swapify.nl</a>
          </p>

        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default TermsPage;
